import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import { getFacilityHierarchyId } from "@utils/facilityUtils";
import {
  countNetworkTotal,
  type FacilityNetworkViewModel,
} from "@utils/facilityNetworkView";
import {
  abbreviateFacilityType,
  facilityDisplayName,
  formatCcnNpiPair,
  networkRegistryDisplayIds,
} from "./facilityNetworkChildrenUtils";

export type ParentChainEntry = {
  facility: FacilityHierarchyInput;
  roleLabel: string;
  showUnlink?: boolean;
};

export type HierarchyRoleDisplay = {
  label: string;
  sublabel: string;
};

function countDescendants(facility: FacilityHierarchyInput): number {
  const children = facility.child_facilities || [];
  return children.reduce((sum, child) => sum + 1 + countDescendants(child), 0);
}

function sameFacility(
  a: FacilityHierarchyInput,
  b: FacilityHierarchyInput,
): boolean {
  const aId = getFacilityHierarchyId(a);
  const bId = getFacilityHierarchyId(b);
  return aId != null && bId != null && aId === bId;
}

function selectedHasChildren(selected: FacilityHierarchyInput): boolean {
  const nested = selected.child_facilities || [];
  if (nested.length > 0) return true;
  return Boolean(selected.is_parent_facility || selected.facility_group_id?.trim());
}

export function isTopLevelParentView(
  view: FacilityNetworkViewModel,
): boolean {
  if (view.isStandalone) return false;
  if (view.selected.parent_facility_group_id?.trim()) return false;
  return sameFacility(view.selected, view.anchor);
}

export function getParentHierarchyInfoBanner(
  view: FacilityNetworkViewModel,
): string | undefined {
  if (isTopLevelParentView(view)) {
    return "No parent linked. This is a top-level facility.";
  }
  return undefined;
}

export function getHierarchyRoleDisplay(
  view: FacilityNetworkViewModel,
): HierarchyRoleDisplay {
  if (view.isStandalone) {
    return {
      label: "Standalone",
      sublabel: "Not linked to a network",
    };
  }

  const { selected } = view;
  const hasParent = Boolean(selected.parent_facility_group_id?.trim());
  const hasChildren = selectedHasChildren(selected);
  const isTopParent = isTopLevelParentView(view);

  if (isTopParent) {
    const below = countDescendants(selected);
    return {
      label: "Top-level parent",
      sublabel:
        below > 0
          ? `${below} facilit${below === 1 ? "y" : "ies"} below`
          : "No facilities below",
    };
  }

  if (hasParent && hasChildren) {
    const below = countDescendants(selected);
    return {
      label: "Middle facility",
      sublabel:
        below > 0
          ? `${below} facilit${below === 1 ? "y" : "ies"} below`
          : "No facilities below",
    };
  }

  if (view.role === "child" || hasParent) {
    return {
      label: "Child",
      sublabel: "No facilities below",
    };
  }

  return {
    label: "Parent",
    sublabel: "No parent above you",
  };
}

export function countLevelsAbove(view: FacilityNetworkViewModel): number {
  if (view.isStandalone) return 0;
  return view.ancestors.length;
}

export function countDirectChildrenOfSelected(
  view: FacilityNetworkViewModel,
): number {
  const nested = view.selected.child_facilities || [];
  if (nested.length > 0) return nested.length;

  if (sameFacility(view.selected, view.anchor)) {
    const anchorId = getFacilityHierarchyId(view.anchor);
    return view.members.filter((m) => getFacilityHierarchyId(m) !== anchorId).length;
  }

  return 0;
}

export function countTotalNetworkForSelected(
  view: FacilityNetworkViewModel,
): number {
  if (view.isStandalone) return 1;
  const subtree = 1 + countDescendants(view.selected);
  if (subtree > 1) return subtree;
  return countNetworkTotal(view);
}

export function getDirectChildrenSublabel(count: number): string {
  if (count === 0) return "None linked yet";
  return `${count} linked facilit${count === 1 ? "y" : "ies"}`;
}

export function getTotalNetworkSublabel(
  view: FacilityNetworkViewModel,
  total: number,
): string {
  if (view.isStandalone || total <= 1) return "Just your facility";
  return "Across all levels";
}

export function getLevelsAboveSublabel(): string {
  return "Parent chain depth";
}

export function getParentHierarchyChain(
  view: FacilityNetworkViewModel,
): FacilityHierarchyInput[] {
  return getParentHierarchyPanelChain(view).map((entry) => entry.facility);
}

function ancestorRoleLabel(index: number, total: number): string {
  const levelsAbove = total - index;
  if (levelsAbove <= 1) return "Parent";
  if (levelsAbove === 2) return "Grandparent";
  if (levelsAbove === 3) return "Great-grandparent";
  return `${levelsAbove - 1} levels above`;
}

/** Facility id used when adding children or sending link requests. Child facilities cannot nest. */
export function getActingParentFacilityId(
  view: FacilityNetworkViewModel | null,
): number | null {
  if (!view) return null;
  if (view.selected.parent_facility_group_id?.trim()) return null;
  return getFacilityHierarchyId(view.selected);
}

/** Selected facility id for listing outgoing child-link rows. Child facilities return null. */
export function getNetworkLinkListFacilityId(
  view: FacilityNetworkViewModel | null,
): number | null {
  if (!view) return null;
  if (view.selected.parent_facility_group_id?.trim()) return null;
  return getFacilityHierarchyId(view.selected);
}

export function getParentHierarchyPanelChain(
  view: FacilityNetworkViewModel,
): ParentChainEntry[] {
  if (view.isStandalone) {
    return [];
  }

  if (!view.selected.parent_facility_group_id?.trim()) {
    if (isTopLevelParentView(view)) {
      return [
        {
          facility: view.selected,
          roleLabel: "You",
        },
      ];
    }
    return [];
  }

  const chain: ParentChainEntry[] = view.ancestors.map((facility, index) => ({
    facility,
    roleLabel: ancestorRoleLabel(index, view.ancestors.length),
    showUnlink: index === view.ancestors.length - 1,
  }));

  chain.push({
    facility: view.selected,
    roleLabel: "You",
  });

  return chain;
}

/**
 * Add Child footer — any selected facility that is not already a child.
 */
export function canShowHierarchyChildAddSection(
  view: FacilityNetworkViewModel,
): boolean {
  return getActingParentFacilityId(view) != null;
}

export function getChildRootsForSelected(
  view: FacilityNetworkViewModel,
): FacilityHierarchyInput[] {
  const nested = view.selected.child_facilities || [];
  if (nested.length > 0) {
    return [view.selected];
  }

  const selectedGroupId = view.selected.facility_group_id?.trim();
  if (selectedGroupId) {
    const directChildren = view.members.filter(
      (member) =>
        member.parent_facility_group_id?.trim() === selectedGroupId &&
        !sameFacility(member, view.selected),
    );
    if (directChildren.length > 0) return directChildren;
    return [view.selected];
  }

  if (sameFacility(view.selected, view.anchor)) {
    const anchorId = getFacilityHierarchyId(view.anchor);
    const directMembers = view.members.filter(
      (m) => getFacilityHierarchyId(m) !== anchorId,
    );
    if (directMembers.length > 0) return directMembers;
    if ((view.anchor.child_facilities || []).length > 0) {
      return [view.anchor];
    }
  }

  return [];
}

export function countFacilitiesInRoots(roots: FacilityHierarchyInput[]): number {
  return roots.reduce((sum, root) => sum + 1 + countDescendants(root), 0);
}

export function facilityIdPrefix(facility: FacilityHierarchyInput): string {
  const { ccn, npi } = networkRegistryDisplayIds(facility);
  return formatCcnNpiPair(ccn, npi);
}

export { facilityDisplayName, abbreviateFacilityType, countDescendants };
